// Narrator as a system: gameplay emits facts on the event bus; this module
// owns the fact → narrator-line policy plus the session-scoped milestone
// state that used to live in main.ts. The engine narrator (queue, cooldowns,
// audio) stays in engine/narrator.ts — this is the only game-side caller.
//
// Subscription handlers run when main drains the bus (after all stages), so
// lines land the same frame their cause happened. Session flags (first
// predator seen, biome crossings) are per-page-load on purpose: the engine
// narrator's persistent oneShot would gate them across reloads; here we want
// one cue per session.

import type { Entity, MutationKind } from '../components'
import type { EventBus } from '../events'
import type { System } from '../scheduler'
import { triggerNarrator, tickNarrator, setNarratorIdle } from '../../engine/narrator'
import { currentBiome, type BiomeId } from '../biomes'

const FIRST_MUTATION_KEY: Record<MutationKind, Parameters<typeof triggerNarrator>[0]> = {
  'bouquet':         'mutation_bouquet_first',
  'static-cling':    'mutation_static_cling_first',
  'nucleus-envy':    'mutation_nucleus_envy_first',
  'wet-socks':       'mutation_wet_socks_first',
  'borrowed-teeth':  'mutation_borrowed_teeth_first',
  'eyes-everywhere': 'mutation_eyes_everywhere_first',
}

export function createNarratorSystems(events: EventBus): System[] {
  // Set each frame by run() before the bus drains, so handlers can compare
  // event entities against the live player.
  let player: Entity | null = null

  let lastDamageMs = -Infinity
  let dnaMilestoneFired = false
  let firstPredatorSeen = false
  const seenBiomeEnter = new Set<BiomeId>()
  let surfaced = false

  events.subscribe('creature.eaten', e => {
    if (e.eater === player) triggerNarrator('first_eat')
  })
  events.subscribe('ally.converted', () => triggerNarrator('first_ally'))
  events.subscribe('predator.eaten', () => triggerNarrator('first_predator_eaten'))
  events.subscribe('predator.windup', () => triggerNarrator('predator_windup'))
  events.subscribe('orb.deathOrbNearby', () => triggerNarrator('first_death_orb'))
  events.subscribe('orb.collected', e => {
    if (e.kind === 'dna') triggerNarrator('dna_orb_pickup')
  })
  events.subscribe('mutation.applied', e => {
    triggerNarrator(e.firstTime ? FIRST_MUTATION_KEY[e.kind] : 'mutation_pickup')
  })
  events.subscribe('mutation.capped', () => triggerNarrator('mutation_capped'))
  events.subscribe('skill.activated', e => {
    if (e.skill === 'size') triggerNarrator('engulf_activated')
  })
  // Speciation reveal — the tier-3 "what did I become?" line, keyed on the
  // resolved critter form. Replaces the generic evolution_tier3 (dropped from
  // the shop's set); fires at the burst the instant the form locks.
  events.subscribe('lineage.speciated', e => triggerNarrator(`speciation_${e.form}` as const))
  events.subscribe('boss.spawned', () => triggerNarrator('boss_spotted'))
  events.subscribe('boss.wake', () => triggerNarrator('boss_wake'))
  events.subscribe('boss.phase2', () => triggerNarrator('boss_phase2'))
  events.subscribe('boss.killed', () => triggerNarrator('boss_killed'))
  events.subscribe('player.lowHealth', () => triggerNarrator('player_low_health'))
  events.subscribe('damage.taken', e => {
    if (e.entity === player) lastDamageMs = performance.now()
  })
  events.subscribe('ally.died', e => {
    if (e.remaining <= 0) {
      triggerNarrator('allies_wiped')
    } else {
      for (let i = 0; i < e.count; i++) triggerNarrator('ally_died')
    }
  })
  events.subscribe('player.died', () => {
    triggerNarrator('first_death')
    triggerNarrator('death')
  })
  // Sealed doors: the blocked line is the lock's telegraph — it names the
  // key (B4 gates) or teaches the far-side rule (B3 shortcuts).
  events.subscribe('door.blocked', e => {
    if (e.kind === 'shortcut') triggerNarrator('door_blocked')
    else if (e.keyId === 'sporulation') triggerNarrator('seal_spore_blocked')
    else if (e.keyId === 'galvanism') triggerNarrator('seal_charge_blocked')
    else triggerNarrator('seal_pass_blocked')
  })
  events.subscribe('door.opened', () => triggerNarrator('door_opened'))
  events.subscribe('key.granted', () => triggerNarrator('hallpass_granted'))
  events.subscribe('portal.cast', () => triggerNarrator('portal_cast'))
  events.subscribe('portal.return', () => triggerNarrator('portal_return'))
  events.subscribe('biome.entered', e => {
    if (e.biome === 'shallow') {
      // Returning to the shallows after any descent — the "surfaced" beat, once.
      if (!surfaced && seenBiomeEnter.size > 0) {
        triggerNarrator('biome_leave_deep')
        surfaced = true
      }
      return
    }
    if (!seenBiomeEnter.has(e.biome)) {
      seenBiomeEnter.add(e.biome)
      triggerNarrator(e.biome === 'mushroom' ? 'biome_enter_mushroom' : 'biome_enter_deep')
    }
  })

  // Biome watcher — emits the biome.entered fact on every crossing; the
  // narrator handler above applies its own once-per-session gating.
  let prevBiome: string | null = null
  const biomeWatch: System = {
    name: 'biome-watch',
    stage: 'effects',
    order: 8,
    run: ctx => {
      const biome = currentBiome(ctx.playerRef.current)
      if (prevBiome !== null && biome !== prevBiome) {
        ctx.events.emit({ type: 'biome.entered', biome })
      }
      prevBiome = biome
    },
  }

  const narrator: System = {
    name: 'narrator',
    stage: 'effects',
    order: 9,
    run: ctx => {
      player = ctx.playerRef.current

      // first_dna_10 milestone
      if (!dnaMilestoneFired && (player.dna ?? 0) >= 10) {
        dnaMilestoneFired = true
        triggerNarrator('first_dna_10')
      }

      // first_predator_seen: fire once when any predator enters camera view
      if (!firstPredatorSeen) {
        const cam = ctx.camera
        const aspect = cam.viewportWidth / cam.viewportHeight
        const z = cam.effectiveZoom
        const halfW = aspect / z
        const halfH = 1 / z
        for (const pred of ctx.world.with('predator', 'position')) {
          if (
            Math.abs(pred.position.x - cam.x) < halfW &&
            Math.abs(pred.position.y - cam.y) < halfH
          ) {
            firstPredatorSeen = true
            triggerNarrator('first_predator_seen')
            break
          }
        }
      }

      // Idle: no predator in windup/lunge AND no player damage in last 10s.
      let anyWindupOrLunge = false
      for (const p of ctx.world.with('predator')) {
        if (p.predator!.state === 'windup' || p.predator!.state === 'lunge') {
          anyWindupOrLunge = true
          break
        }
      }
      const noDamageRecently = performance.now() - lastDamageMs > 10_000
      setNarratorIdle(!anyWindupOrLunge && noDamageRecently)
      tickNarrator(ctx.dt)
    },
  }

  return [biomeWatch, narrator]
}
